import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useEffect, useState } from 'react'
import { Alert, Container, Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { TailSpin } from "react-loader-spinner";
import AppSearch from '../../components/common/AppSearch';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { accessLevelOptions, createQueryDetails, datasourceOptions, getConnectionDetails } from '../../utils';
import { Select } from "antd";
import { useMemo } from "react";
import { debounce } from "lodash";
import { Datasources } from "../../api/Datasources";
import DatasourceItem from "../../components/list-items/DatasourceItem";
import { VizClient } from '../../api/VizClient';
import { Datasource } from '../../models/Datasource';
import { AccessLevel } from '../../models/generics';


function DatasourcesList() {
  const [datasources, setDatasources] = useState<Datasource[] | null>();
  // const [totalDatasets, setTotalDatasets] = useState();
  const [error, setError] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [accessLevelFilter, setAccessLevelFilter] = useState<AccessLevel>();
  const [typeFilter, setTypeFilter] = useState<string>();
  // const [myDatasets, setMyDatasets] = useState(false);
  // const [itemsPerPage, setItemsPerPage] = useState(20);
  // const [currentPage, setCurrentPage] = useState(1);

  // const [sortDesc, setSortDesc] = useState(true);

  const [datasource, setDatasource] = useState<Datasource | null>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  useEffect(() => {
    getDatasources()
    // eslint-disable-next-line
  }, []);
  // useEffect(() => {
  //   reset();
  //   // eslint-disable-next-line
  // }, [currentPage]);

  // handling current page higher than actual available pages edge case
  // useEffect(() => {
  //   if (currentPage > 1) setCurrentPage(1);
  //   else reset();
  //   // eslint-disable-next-line
  // }, [itemsPerPage, sortDesc, searchText, statusFilter, accessLevelFilter, myDatasets]);

  const getDatasources = () => {
    Datasources.getDatasources()
      .then(
        ({ data }) => setDatasources(data),
        (err) => {
          showToast('datasources-error', 'Could not get datasources', 'Datasources Error', 'danger');
          setDatasources([]);
          setError(true);
        }
      );
  }

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setDatasource(null);
  };

  const navigateToCreation = () => navigate('/datasources/create');
  // const changeSortMode = () => setSortDesc(!sortDesc);

  const reset = () => {
    setError(false);
    setDatasources(null);
    getDatasources();
  }

  const editDatasource = (datasource: Datasource) => (evt: React.MouseEvent) => {
    evt.stopPropagation();
    navigate(`/datasources/${datasource.id}/edit`);
  }

  const testDatasource = (datasource: Datasource) => (evt: React.MouseEvent) => {
    evt.stopPropagation();
    showLoader();
    const connectionDetails = getConnectionDetails(datasource);
    const body = createQueryDetails(connectionDetails);

    const promise = datasource.type === 'messageBroker'
      ? VizClient.getTopics(body, datasource.driverType)
      : VizClient.getDatabases(body, datasource.driverType);

    promise.then(
      ({ data: res }) => {
        hideLoader();
        if (!res) showToast('connection-error', "Error while connecting to data source", "Connection Error", 'danger');
        else showToast('connection-success', "Successfully connected to data source", "Connection Valid", 'success');
      },
      err => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('connection-error', message, `Connection Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }

  const onDeleteDatasource = (datasource: Datasource) => (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setDatasource(datasource);
  }

  const deleteDatasource = () => {
    showLoader();

    Datasources.deleteDatasource(datasource?.id as number)
      .then(
        () => {
          hideLoader();
          reset();
          handleDeleteClose();
          showToast('datasource-success', 'Data Source deleted successfully', 'Data Source Deleted', 'success');
        },
        () => {
          hideLoader();
          showToast('datasource-error', 'Could not delete data source', 'Data Source Error', 'danger');
        }
      )
  }

  const handleFilterChange = (filter: 'search' | 'accessLevel' | 'type') => (evt: any) => {
    switch (filter) {
      case 'search':
      default:
        setSearchText(evt.target.value);
        break;

      case 'accessLevel':
        setAccessLevelFilter(evt);
        break;

      case 'type':
        setTypeFilter(evt);
        break;
    }
  }

  const debounceSearchChange = useMemo(() => debounce(handleFilterChange("search"), 500), []);

  // const handleItemsPerPageChange = (page, size) => {
  //   changePage(page);
  //   setItemsPerPage(size);
  // }
  // const changePage = (page) => setCurrentPage(page);
  // const handleMyDatasetsChange = (evt) => setMyDatasets(evt.target.checked);
  const datasourceDetail = (datasource: Datasource) => () => navigate(`/datasources/${datasource.id}`);

  // cleanup debounced handler
  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    }
  }, [debounceSearchChange]);

  const filteredDatasources = useMemo(() => {
    return datasources
      ?.filter(datasource => !typeFilter || datasource.type === typeFilter)
      .filter(datasource => !accessLevelFilter || datasource.accessLevel === accessLevelFilter)
      .filter(datasource => datasource.name.toLowerCase().includes(searchText.toLowerCase())) || null;
  }, [datasources, searchText, typeFilter, accessLevelFilter]);

  const renderDatasources = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return filteredDatasources?.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No data sources found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            filteredDatasources?.map(datasource => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={datasource.id}>
                <DatasourceItem
                  datasource={datasource}
                  onClick={datasourceDetail(datasource)}
                  onDelete={onDeleteDatasource(datasource)}
                  onEdit={editDatasource(datasource)}
                  onTest={testDatasource(datasource)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  const renderAsyncContent = () => {
    return datasources
      ? renderDatasources()
      : (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      );
  }

  const renderToolbar = () => {
    return (
      <>
        <Row className="mb-2 align-items-center">
          <Col>
            <h4 className="mb-0">Data Sources List</h4>
          </Col>

          <Col xs={6} md={4} className="text-end">
            <Button size="sm" variant="success" onClick={navigateToCreation}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="d-none d-sm-inline ms-2">Register Data Source</span>
            </Button>
          </Col>
        </Row>

        <Row className="align-items-center">
          <Col md={3} className="mb-2">
            <AppSearch placeholder="Search data source..." onChange={debounceSearchChange} />
          </Col>
          <Col md={2} className="mb-2">
            <Select
              allowClear
              placeholder="Select privacy level"
              value={accessLevelFilter}
              onChange={handleFilterChange('accessLevel')}
              className="w-100"
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col md={2} className="mb-2">
            <Select
              allowClear
              placeholder="Select data source type"
              value={typeFilter}
              onChange={handleFilterChange('type')}
              className="w-100"
            >
              {datasourceOptions.map((option, index) => (
                <Select.Option key={index} value={option.value} label={option.label}>
                  <div>
                    <FontAwesomeIcon icon={option.icon} fixedWidth />
                    <span className="ms-2">{option.label}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Col>
          {filteredDatasources && (
            <Col md={5} className="text-end">
              {filteredDatasources.length > 0 && <h6 className="text-muted mb-0">({filteredDatasources.length} found)</h6>}
            </Col>
          )}
        </Row>
        {/* <Row>
          <Col md={2}>
            <Checkbox checked={myDatasets} onChange={handleMyDatasetsChange}>My Datasets</Checkbox>
            <Checkbox className="ms-3" checked={defaultOnly} onChange={handleDefaultOnlyChange}>Default Only</Checkbox>
            <Checkbox className="ms-3" checked={activeSchedules} onChange={handleSchedulesChange}>Active Schedules</Checkbox>
          </Col>
          <Col className="text-center">
            <Pagination
              disabled={!datasources}
              total={totalDatasets}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              pageSize={itemsPerPage}
              current={currentPage}
              onChange={changePage}
              onShowSizeChange={handleItemsPerPageChange}
            />
          </Col>
          <Col md={2}>
            <Button size="sm" variant="light" className="w-100 border" onClick={changeSortMode}>
              <FontAwesomeIcon icon={sortDesc ? solid('sort-numeric-down-alt') : solid('sort-numeric-down')} />
              <span className="ms-2">Sort: <b>{sortDesc ? "descending" : "ascending"}</b></span>
            </Button>
          </Col>
        </Row> */}
      </>
    )
  }

  return (
    <Container fluid="md" className="mt-3">
      {renderToolbar()}

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Data Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete data source <b>{datasource && datasource.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteDatasource}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default memo(DatasourcesList);
